if (!customElements.get('product-customisation')) {
  customElements.define(
    'product-customisation',
    class ProductCustomisation extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.checkbox = this.querySelector('[data-customisation-toggle]');
        this.fields = this.querySelector('[data-customisation-fields]');
        this.blankNote = this.querySelector('[data-customisation-blank-note]');
        this.input = this.querySelector('[data-customisation-input]');
        this.counter = this.querySelector('[data-customisation-counter]');
        this.error = this.querySelector('[data-customisation-error]');
        this.form = this.input?.form;

        if (!this.checkbox || !this.fields || !this.input || !this.counter || !this.error || !this.form) return;

        this.initialized = true;
        this.validCharacters = /^[A-Za-z0-9 ]*$/;
        this.validValue = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ]{1,15}$/;
        this.onToggle = this.handleToggle.bind(this);
        this.onInput = this.handleInput.bind(this);
        this.onBlur = this.handleBlur.bind(this);
        this.onPaste = this.handlePaste.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);

        this.checkbox.addEventListener('change', this.onToggle);
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('blur', this.onBlur);
        this.input.addEventListener('paste', this.onPaste);
        this.form.addEventListener('submit', this.onSubmit, true);

        this.classList.add('is-enhanced');
        this.updateCounter();
        this.setExpanded(this.checkbox.checked);
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.checkbox.removeEventListener('change', this.onToggle);
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('blur', this.onBlur);
        this.input.removeEventListener('paste', this.onPaste);
        this.form.removeEventListener('submit', this.onSubmit, true);
        this.initialized = false;
      }

      handleToggle() {
        this.setExpanded(this.checkbox.checked);
      }

      setExpanded(expanded) {
        this.checkbox.setAttribute('aria-expanded', String(expanded));
        this.fields.hidden = !expanded;
        if (this.blankNote) this.blankNote.hidden = expanded;
        this.input.disabled = !expanded;
        this.input.required = expanded;

        if (!expanded) this.clearError();
      }

      handleInput() {
        this.updateCounter();

        if (this.input.value && !this.validCharacters.test(this.input.value)) {
          this.showError(this.dataset.invalidMessage);
          return;
        }

        this.clearError();
      }

      handleBlur() {
        if (this.checkbox.checked && this.input.value) this.validate();
      }

      handlePaste(event) {
        const pastedText = event.clipboardData?.getData('text');
        if (!pastedText || !/[\t\r\n]/.test(pastedText)) return;

        event.preventDefault();
        this.showError(this.dataset.invalidMessage);
      }

      handleSubmit(event) {
        if (!this.checkbox.checked) return;

        this.input.value = this.input.value.trim();
        this.updateCounter();

        if (this.validate()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.input.reportValidity();
        this.input.focus({ preventScroll: false });
      }

      validate() {
        const value = this.input.value;
        this.input.setCustomValidity('');

        if (!this.input.checkValidity() || !this.validValue.test(value)) {
          this.showError(this.input.validationMessage || this.dataset.invalidMessage);
          return false;
        }

        this.clearError();
        return true;
      }

      showError(message) {
        const errorMessage = message || this.dataset.invalidMessage;
        this.input.setCustomValidity(errorMessage);
        this.input.setAttribute('aria-invalid', 'true');

        const visibleMessage = `${this.dataset.errorLabel}: ${errorMessage}`;
        if (this.error.textContent !== visibleMessage) this.error.textContent = visibleMessage;
        this.error.hidden = false;
      }

      clearError() {
        this.input.setCustomValidity('');
        this.input.setAttribute('aria-invalid', 'false');
        this.error.hidden = true;
      }

      updateCounter() {
        const count = this.input.value.length;
        this.counter.textContent = `${count} / 15`;
        this.counter.setAttribute('aria-label', `${count}. ${this.dataset.counterLabel}`);
      }
    }
  );
}
